export const situationAnswerKeys = ["cash", "epp", "retail", "curious", "other"] as const;
export const followUpAnswerKeys = ["cash", "epp", "retail", "other"] as const;
export type SituationAnswer = (typeof situationAnswerKeys)[number];
export type FollowUpAnswer = (typeof followUpAnswerKeys)[number];
export const associateQuestionNames = ["situation", "timeline", "workLevel"] as const;
export type AssociateQuestionName = (typeof associateQuestionNames)[number];

export const recommendationKeys = ["cash", "epp", "retail", "curious", "mixed"] as const;
export type RecommendationKey = (typeof recommendationKeys)[number];

export function recommend(
	situation: SituationAnswer,
	timeline: FollowUpAnswer,
	workLevel: FollowUpAnswer
): RecommendationKey {
	if (situation === "curious") return "curious";
	const answers = [situation, timeline, workLevel];
	for (const key of ["cash", "epp", "retail"] as const) {
		if (answers.filter((answer) => answer === key).length >= 2) return key;
	}
	return "mixed";
}

export function streetLineFrom(input: {
	address: string;
	addressStreetNumber?: string;
	addressRoute?: string;
}): string {
	const streetNumber = input.addressStreetNumber?.trim() ?? "";
	const route = input.addressRoute?.trim() ?? "";
	const line = streetNumber && route ? `${streetNumber} ${route}` : input.address.split(",")[0];
	return line.trim().replace(/\s+/g, " ");
}

export type AssociateLeadInput = {
	situation: SituationAnswer;
	situationOther: string;
	timeline: FollowUpAnswer;
	timelineOther: string;
	workLevel: FollowUpAnswer;
	workLevelOther: string;
	address: string;
	addressStreetNumber: string;
	addressRoute: string;
	name: string;
	phone: string;
	email: string;
	associateSlug: string;
	pageUrl: string;
	routeId: string;
	utmSource: string;
	utmMedium: string;
	utmCampaign: string;
	utmContent: string;
	utmTerm: string;
};

export const associateAnswers = {
	situation: [
		{ key: "cash", label: "I need it sold, and soon." },
		{ key: "epp", label: "I'd sell for the right number, but I don't want the hassle." },
		{ key: "retail", label: "I want top dollar and I'm willing to work for it." },
		{ key: "curious", label: "Honestly? I'm just curious what it's worth." },
		{ key: "other", label: "None of those fit. Here's my situation." }
	] satisfies { key: SituationAnswer; label: string }[],
	timeline: [
		{ key: "cash", label: "A week or two. It's urgent." },
		{ key: "epp", label: "A few months is fine. I'm not in a crisis." },
		{ key: "retail", label: "No deadline at all. I can wait it out." },
		{ key: "other", label: "It depends. Let me explain." }
	] satisfies { key: FollowUpAnswer; label: string }[],
	workLevel: [
		{ key: "cash", label: "None. I don't want anyone walking through it." },
		{ key: "epp", label: "Reasonable access to my home, yes. Repairs and cleanout, no." },
		{ key: "retail", label: "Repairs, paint, staging, whatever it takes." },
		{ key: "other", label: "It's complicated. Let me explain." }
	] satisfies { key: FollowUpAnswer; label: string }[]
};

export const associateResults: Record<
	RecommendationKey,
	{ heading: string; body: string; nextStep: string }
> = {
	cash: {
		heading: "Selling to us for cash is probably your best fit.",
		body: "Speed and certainty are what you're solving for, and a cash offer is built for exactly that. Fergmar can close in as little as a week, with no access arrangements and no repairs. You'd trade some price for that. If the gap between our cash number and what you need turns out to be meaningful, the program is still worth a look.",
		nextStep:
			"Next step: we give you a real cash number on the property. Free, and you're under no obligation to take it."
	},
	epp: {
		heading: "The Equity Protection Program looks like a strong fit.",
		body: "You're not in a crisis on timing, you don't want to repair or manage a traditional sale, and you'd like to protect more of your equity than a quick cash close typically allows. That's precisely the situation this program was built for.",
		nextStep:
			"Next step: a short conversation about the property and what the current market can realistically support."
	},
	retail: {
		heading: "The open market may serve you better.",
		body: "If you're willing to do repairs, give months of access to your home, and ride out inspections, appraisals, and buyer financing, taking it to the open market usually reaches the highest gross price, though it typically runs three to six months start to finish. That's the honest answer, even though it isn't the one that helps us.",
		nextStep:
			"Still worth a call: we'll walk you through what you'd actually net either way, so you're choosing with real numbers instead of a guess."
	},
	curious: {
		heading: "Then let's start with what it's actually worth.",
		body: "Plenty of people call us with no intention of selling anything. They want to know what they're sitting on, for an inherited house, a rental they're tired of, a place that's been empty a while, or just because they've never had a straight answer. That's a fine reason to call.",
		nextStep:
			"Next step: we'll pull recent closed sales near you and give you a real number. No cost, no obligation, and no one will chase you afterward."
	},
	mixed: {
		heading: "You're between two options. Worth a conversation.",
		body: "Your answers point in more than one direction, which is common and not a problem. It usually means the right call depends on details a few questions can't see: the condition of the property, your timeline, and what you need to walk away with.",
		nextStep: "Next step: a fifteen-minute phone call, not a form."
	}
};

export function answerLabel(question: AssociateQuestionName, key: string): string {
	return associateAnswers[question].find((answer) => answer.key === key)?.label ?? key;
}
